import { isEmpty } from 'lodash';
import { FC, useCallback, useEffect, useState } from 'react';

import { WebPushContentDefinition } from 'modules/graphql/types';
import { useGetTemplateUserJsonTestData } from 'modules/templateEditor/hooks/useGetTemplateUserJsonTestData';

import { PreviewWithDataErrorMessage, TemplateEditViews } from '../../common/types';
import { getContentWithData, getPreviewWithDataError } from '../../common/utils';
import { useEditableFieldsContext } from '../../context/EditableFieldsContext';
import { usePreviewWithDataContext } from '../../context/PreviewWithDataContext';
import { useQueryParamContext } from '../../context/QueryParamContext';
import { WebPushPreviewContext, WebPushPreviewContextState } from './WebPushPreviewContext';

export const WebPushPreviewContextProvider: FC<React.PropsWithChildren<unknown>> = ({
  children,
}) => {
  const { liveTemplate } = useEditableFieldsContext();
  const { canRefreshPreview, dataFeedData, userData, isUserDataDirty, setIsUserDataDirty } =
    usePreviewWithDataContext();
  const { templateId, view } = useQueryParamContext();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [messageContentBody, setMessageContentBody] = useState<string>('');
  const [messageContentTitle, setMessageContentTitle] = useState<string>('');
  const [messageContentClickAction, setMessageContentClickAction] = useState<string>('');
  const [previewError, setPreviewError] = useState<PreviewWithDataErrorMessage | undefined>(
    undefined,
  );

  const webPushData = (liveTemplate?.mediumData || {}) as WebPushContentDefinition;
  const hasContent = !isEmpty(webPushData);
  const { message, title, clickAction } = webPushData;
  const isPreviewWithData = view === TemplateEditViews.preview;
  const locale = webPushData?.locale?.name || '';

  const { getTemplateUserJsonTestData } = useGetTemplateUserJsonTestData();

  const onRefreshPreview = useCallback(async () => {
    // Only make request when template data has been received
    if (canRefreshPreview && hasContent) {
      setIsLoading(true);

      const userDataToSave = await getTemplateUserJsonTestData(userData, isUserDataDirty);

      getContentWithData(
        templateId,
        {
          subject: clickAction ?? '',
          webBody: message ?? '',
          webTitle: title ?? '',
        },
        JSON.parse(dataFeedData),
        JSON.parse(userDataToSave),
      )
        .then(({ data }) => {
          setIsUserDataDirty(false);
          setMessageContentClickAction(data.subject);
          setMessageContentBody(data.webBody);
          setMessageContentTitle(data.webTitle);
          setPreviewError(undefined);
        })
        .catch(({ response }) => {
          setPreviewError(getPreviewWithDataError(response?.data?.message || ''));
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
  }, [
    canRefreshPreview,
    dataFeedData,
    message,
    title,
    clickAction,
    templateId,
    userData,
    isUserDataDirty,
    setIsUserDataDirty,
    getTemplateUserJsonTestData,
  ]);

  /* Note: we only want this useEffect to run when dataFeedData or userData change even though it is
   * also dependent on onRefreshPreview. However, onRefreshPreview shares ALL of the useEffect's
   * dependencies, so the version of onRefreshPreview used will always be current.
   * Including onRefreshPreview as a dependency here would cause an unintended side-effect of
   * running onRefreshPreview on each change of fullSmsMessage */
  useEffect(() => {
    if (canRefreshPreview) {
      onRefreshPreview();
    }
  }, [canRefreshPreview, dataFeedData, userData, locale, hasContent]);

  const context: WebPushPreviewContextState = {
    isLoading,
    messageContentBody,
    messageContentTitle,
    messageContentClickAction,
    previewError,
    onRefreshPreview,
    isPreviewWithData,
  };

  return (
    <WebPushPreviewContext.Provider value={context}>{children}</WebPushPreviewContext.Provider>
  );
};
